import { ChangeEvent } from 'react';
import { FormHelperText, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import { CoreBox, CoreFormControl } from '@youscience/core';
import {
  GpaWeighting,
  RegGpaScale,
  OptionalReportRequest,
  OptionalReportSchema,
} from '@youscience/commonapp-service-common';

import RadioButtonGroupWithClearButton from '@components/RadioGroupWithClearButton';
import MonthYearDatePicker from '@components/MonthYearDatePicker';

import { REQUIRED_FIELD_MESSAGE } from '@constants/texts';
import { FormState } from '@constants/commonApp/types';

import ContinueButton from '@components/ContinueButton';
import SectionAccordion from '@components/SectionAccordion';

import { dateRangeValidation, handleZodValidation } from '../../../../common/formUtils';

import { sxStyles } from './GPA.styles';
import { maxDate, minDate } from '@pages/RecommenderSpace/components/constants';
import { useApplicantFormSection } from '@pages/StudentDetailsPage/hooks/useApplicantFormSection';

export type GPAFields = NonNullable<Partial<OptionalReportRequest['gpa']>>;

interface GPAProps {
  gpa?: GPAFields;
  onFormChange: (formState: FormState<GPAFields>) => void;
  onExpand: () => void;
  onContinue?: () => void;
  isExtended: boolean;
  isSaved: boolean;
  showError?: boolean;
}

const additionalValidations = (newFormState: FormState<GPAFields>) => {
  return dateRangeValidation(newFormState, {});
};

const validator = OptionalReportSchema._def.schema.shape.gpa;

export const GPA = ({ gpa = {}, onFormChange, onExpand, onContinue, isExtended, isSaved, showError }: GPAProps) => {
  const [formState, setFormStateWithErrors] = useApplicantFormSection<GPAFields, typeof validator>(
    gpa,
    validator,
    isSaved,
    onFormChange,
    additionalValidations,
  );

  const isShowErrors = new URLSearchParams(window.location.search).get('showErrors') === 'true';

  const handleDateChange = (fieldName: string) => (value: Date | null) => {
    if (value) {
      setFormStateWithErrors({ [fieldName]: `${value.getMonth() + 1}/${value.getFullYear()}`.padStart(7, '0') });
    } else {
      setFormStateWithErrors({ [fieldName]: undefined });
    }
  };

  const handleSelectChange = (field: string) => (event: SelectChangeEvent<unknown>) => {
    setFormStateWithErrors({ [field]: (event.target.value as string) || '' });
  };

  const handleTextFieldChange =
    (field: string, { optional = false } = {}) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.value && handleZodValidation(field, validator, { [field]: event.target.value }).isError) return;
      setFormStateWithErrors({ [field]: event.target.value || (optional ? null : undefined) });
    };

  const handleRadioChange = (field: string) => (event: ChangeEvent<HTMLInputElement>) => {
    setFormStateWithErrors({ [field]: event.target.value });
  };

  return (
    <SectionAccordion
      title="GPA"
      isExpanded={isExtended}
      onChange={onExpand}
      isChanged={formState.hasChanged}
      showCheckmark={formState.isSaved && !!formState.isFormValid}
      isError={showError || !formState.isFormValid}
    >
      <CoreBox sx={sxStyles.accordionDetails}>
        <CoreBox sx={sxStyles.flexPosition}>
          <CoreFormControl fullWidth>
            <TextField
              name="cumulative"
              label="Cumulative GPA"
              value={formState?.data?.cumulative ?? ''}
              required
              error={!!formState.errors.cumulative}
              helperText={formState.errors.cumulative}
              onChange={handleTextFieldChange('cumulative')}
            />
          </CoreFormControl>

          <CoreFormControl fullWidth error={!!formState.errors.scale}>
            <InputLabel required>GPA scale</InputLabel>

            <Select label="GPA scale" value={formState?.data?.scale ?? ''} onChange={handleSelectChange('scale')}>
              {Object.entries(RegGpaScale).map(([key, value]) => {
                return (
                  <MenuItem key={value} value={value}>
                    {key}
                  </MenuItem>
                );
              })}
            </Select>

            {formState.errors.scale && <FormHelperText error>{REQUIRED_FIELD_MESSAGE}</FormHelperText>}
          </CoreFormControl>
        </CoreBox>

        <CoreBox sx={sxStyles.flexPosition}>
          <MonthYearDatePicker
            name="startDate"
            label="From (start date)"
            required
            slotProps={{
              inputProps: { clearable: true, onClear: () => handleDateChange('startDate') },
              error: !formState?.data?.startDate || Boolean(formState.errors['startDate']),
            }}
            value={formState?.data?.startDate ?? undefined}
            error={!formState?.data?.startDate ? REQUIRED_FIELD_MESSAGE : formState.errors.startDate}
            handleDateChange={handleDateChange('startDate')}
            minDate={minDate}
            maxDate={maxDate}
          />

          <MonthYearDatePicker
            name="endDate"
            label="Ending date (final date)"
            required
            slotProps={{
              inputProps: { clearable: true, onClear: () => handleDateChange('endDate') },
              error: !formState?.data?.endDate || Boolean(formState.errors['endDate']),
            }}
            value={formState?.data?.endDate ?? undefined}
            error={!formState?.data?.endDate ? REQUIRED_FIELD_MESSAGE : formState.errors.endDate}
            handleDateChange={handleDateChange('endDate')}
            minDate={minDate}
            maxDate={maxDate}
          />
        </CoreBox>

        <RadioButtonGroupWithClearButton
          title="GPA weighting"
          name="weighting"
          value={formState?.data?.weighting ?? undefined}
          options={GpaWeighting}
          required
          handleRadioChange={handleRadioChange}
          handleClear={() => setFormStateWithErrors({ weighting: undefined })}
          error={!!formState.errors.weighting}
          helperText={formState.errors.weighting}
        />

        <CoreBox sx={sxStyles.flexPosition}>
          <CoreFormControl fullWidth>
            <TextField
              name="passing"
              label="School's passing mark"
              value={formState?.data?.passing ?? ''}
              required
              error={!!formState.errors.passing}
              helperText={formState.errors.passing}
              onChange={handleTextFieldChange('passing')}
            />
          </CoreFormControl>

          <CoreFormControl fullWidth>
            <TextField
              name="highest"
              label="Highest grade/GPA in class"
              value={formState?.data?.highest ?? ''}
              onChange={handleTextFieldChange('highest', { optional: true })}
            />
          </CoreFormControl>
        </CoreBox>

        {onContinue && <ContinueButton onContinue={onContinue} />}
      </CoreBox>
    </SectionAccordion>
  );
};
